import React, { useCallback, useState } from 'react';
import { isNull } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import styles from '../Clients.module.scss';
import { forgiveRejectedQuestionsStarted, getRejectedQuestionsToForgive, toggleRejectedQuestion } from '../store';
import { RejectedQuestion, RejectedQuestionsTab, RejectedQuestionsToForgive } from '../store/types';
import { formatDate } from '../../../../utils/luxon';
import { Toggle } from '../../../shared/toggle';
import { Button } from '../../../shared/button/Button';

const { red, amber, green, grey } = styles;

interface RejectedQuestionsForUserProps {
    userId: number;
    questions: RejectedQuestion[];
    currentTab: RejectedQuestionsTab;
}

export const RejectedQuestionsForUser: React.FC<RejectedQuestionsForUserProps> = ({ userId, questions, currentTab }) => {
    const dispatch = useAppDispatch();

    const [hoveredQuestionId, setHoveredQuestionId] = useState<number | null>(null);

    const rejectedQuestionsToForgive = useAppSelector(getRejectedQuestionsToForgive);

    const toggleQuery = useCallback((userId: number, questionId: number) => {
        dispatch(toggleRejectedQuestion(userId, questionId));
    }, [dispatch]);

    const forgiveSingleQuestion = useCallback((question: RejectedQuestionsToForgive) => dispatch(forgiveRejectedQuestionsStarted([question])), [dispatch]);

    const questionIsSelected = useCallback((userId: number, questionId: number) => !!rejectedQuestionsToForgive.find(rejectedForUser => rejectedForUser.userId === userId)?.questionIds.includes(questionId), [rejectedQuestionsToForgive]);

    const questionRejectionLevel = useCallback((seriousRejection: boolean) => seriousRejection ? { label: 'Serious Rejection', color: red } : { label: 'Unsuitable Question', color: amber }, []);

    return (
        <div className={styles.questionsWrapper}>
            {questions.length > 0 ? questions.map((question, index) => {
                const { documentCustomQueryId, questionAsked, dateAsked, seriousRejection, forgivenBy, forgivenDate, documentName } = question;
                const isForgiven = !isNull(forgivenBy);
                const { label, color } = questionRejectionLevel(seriousRejection);
                const showForgiveButton = hoveredQuestionId === documentCustomQueryId;
                const questionSelected = questionIsSelected(userId, documentCustomQueryId);
                const border = `${seriousRejection ? red : grey} solid ${seriousRejection ? 2 : 1}px`;
                return (
                    <div key={index} className={styles.questionContentWrapper} style={{ border }} onMouseEnter={() => setHoveredQuestionId(documentCustomQueryId)} onMouseLeave={() => setHoveredQuestionId(null)}>
                        {!isForgiven && <div className={styles.checkboxWrapper}>
                            <Toggle checked={questionSelected} onChange={() => toggleQuery(userId, documentCustomQueryId)} />
                        </div>}
                        <div className={styles.contentWrapper}>
                            <div className={styles.rejectionType} style={{ color }}>{label}</div>
                            <div className={styles.questionAsked}>{questionAsked}</div>
                            <div className={styles.footerWrapper}>
                                <div className={styles.footerMeta}>
                                    <div className={styles.metaField}>
                                        <div className={styles.contentTitle}>Document Name:</div>
                                        <div className={styles.contentValue}>{documentName}</div>
                                    </div>
                                    <div className={styles.metaField}>
                                        <div className={styles.contentTitle}>Asked On:</div>
                                        <div className={styles.contentValue}>{formatDate(dateAsked)}</div>
                                    </div>
                                    {isForgiven && forgivenDate && <div className={styles.metaField}>
                                        <div className={styles.contentTitle} style={{ color: green }}>Forgiven By:</div>
                                        <div className={styles.contentValue}>{forgivenBy} on {formatDate(forgivenDate)}</div>
                                    </div>}
                                </div>
                                {!isForgiven &&
                                    <div className={styles.forgiveButton}>
                                        {showForgiveButton && <Button margin='0' width='fit-content' label='Forgive' onClick={() => forgiveSingleQuestion({ userId, questionIds: [documentCustomQueryId] })} />}
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                );
            }) : <div className={styles.noQuestionsPlaceholder}>No {currentTab}</div>}
        </div>
    );
};
